import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';

export function useLayout() {
    const store = useStore();
    const route = useRoute();
    const display = useDisplay();

    const isLoggedIn = computed<boolean>(() => store.state.auth.isLoggedIn);
    const userEmail = computed<string | null>(() => store.state.auth.user?.email || null);
    const currentUser = computed(() => store.state.auth.currentUser);

    const sidebarBehaviorForLargeScreen = computed<'expanded' | 'rail' | undefined>(
        () => store.getters['navigation/getSidebarBehaviorForLargeScreen'],
    );

    const isMainLayoutActive = computed(() => {
        const hasUserInfo = userEmail.value || currentUser.value?.email;
        return (
            isLoggedIn.value && hasUserInfo && route.name !== 'login' && route.name !== 'htmlReport'
        );
    });

    const isSystemBarExpected = computed(() => {
        const appMode = process.env.VUE_APP_MODE;
        // System bar is active if VUE_APP_MODE is demo, qa, or dev
        if (appMode === 'demo' || appMode === 'qa' || appMode === 'dev') {
            return true;
        }
        // Or if no specific VUE_APP_MODE is set, but it's a development build (NODE_ENV)
        if (!appMode && process.env.NODE_ENV === 'development') {
            return true;
        }
        // Otherwise, it's considered inactive (e.g., production without specific VUE_APP_MODE)
        return false;
    });

    const dynamicAppClasses = computed(() => {
        if (!isMainLayoutActive.value) {
            return []; // No dynamic classes if main layout isn't active
        }

        const classes = [];
        if (display.lgAndUp.value) {
            classes.push('view-desktop');
            if (sidebarBehaviorForLargeScreen.value === 'expanded') {
                classes.push('desktop-sidebar-expanded');
            } else if (sidebarBehaviorForLargeScreen.value === 'rail') {
                classes.push('desktop-sidebar-rail');
            } else {
                classes.push('desktop-sidebar-none'); // For cases with no persistent desktop sidebar
            }
        } else {
            classes.push('view-mobile');
        }

        if (isSystemBarExpected.value) {
            classes.push('system-bar-active');
        } else {
            classes.push('system-bar-inactive');
        }
        return classes;
    });

    return {
        dynamicAppClasses,
        isMainLayoutActive,
    };
}
